import { bind } from 'bind-decorator';
import { uniqBy } from 'lodash';
import type { IEpisodeCategoryOneService, IService } from '@common/interfaces';
import type { IPatient } from '@common/interfaces/patient';
import type { UnavailableService } from '@common/interfaces/patientUnavailableServices';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import type { DecisionAidPlacement } from '@frontend/components/elements/DecisionAid';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import type { EpisodeCategory } from '../../../../../common/enums/episodeCategory';
import type {
    IServiceStartingPoint,
    IServiceTypeWithOneService,
} from '../../../../../common/interfaces/serviceWorkflow';
import type { EpisodeCategoryMapType, EpisodeCategoryServicesMapType, ServiceMapType } from './api/newProcessApi';
import type { ISelectedService } from './newProcessInterfaces';
import { ServiceType } from '../../../../../common/enums/serviceType';
import { AbortableFetch } from '../../../../store/fetch';
import { Component } from '../../../elements/Component';
import { addTrackEvent } from '../../customControls/utils';
import { findUnavailableServices } from '../../patients/searchPatient/searchPatientFunctions';
import { NewProcessApi } from './api/newProcessApi';
import {
    newProcessCanRenderCustomControl,
    newProcessLoadCustomControl,
} from './customControls/newProcessCustomControls';
import { NewProcessActionButtonRow } from './wizardRow/NewProcessActionButtonRow';
import { NewProcessNameButtonRow } from './wizardRow/NewProcessNameButtonRow';
import { NewProcessServiceButtonRow } from './wizardRow/NewProcessServiceButtonRow';
import { NewProcessStartingPointButtonRow } from './wizardRow/NewProcessStartingPointButtonRow';
import { NewProcessTypeButtonRow } from './wizardRow/NewProcessTypeButtonRow';

interface INewProcessButtonStartBarProps extends IWithRouterProps {
    decisionAidPlacement?: DecisionAidPlacement;
    defaultSelectedEpisodeCategory?: EpisodeCategory;
    defaultSelectedService?: ISelectedService;
    defaultSelectedServiceType?: ServiceType;
    /** Should the default selected props trigger the onClick function for the specified button rows */
    defaultTriggerOnClick?: {
        episodeCategory?: boolean;
        service?: boolean;
        serviceType?: boolean;
    };
    hideEpisodeCategoryBar?: boolean;

    label: string;

    onClear?: () => void;

    onEpisodeCategorySelected?: (episodeCategory: EpisodeCategory | undefined) => void;
    onLoad?: () => Promise<void>;
    onServiceIdSelected?: (args: ServiceUrlObject) => Promise<void> | void;
    onServiceTypeSelected?: (serviceType: ServiceType) => void;
    onStartingPointSelected: (startingPoint: IServiceStartingPoint) => Promise<void> | void;

    patient?: IPatient;
    scrollToBottom?: () => void;
    showServiceTypeBar?: boolean;
}

export interface INewProcessButtonStartBarState {
    autoSelectedEpisodeCategory: boolean;
    autoSelectedService: boolean;

    clearState?: boolean;
    episodeCategories: IEpisodeCategoryOneService[];

    episodeCategoriesByServiceType: { [key in string]: string[] };
    episodeCategoryMap?: EpisodeCategoryMapType;

    newProcessCustomIdentifier: string | undefined;
    onStateCleared?: () => Promise<void>;

    selectedActionWithProcedure: boolean | undefined;

    selectedEpisodeCategory: EpisodeCategory | undefined;
    selectedService: ISelectedService | undefined;

    selectedServiceType: ServiceType | undefined;
    selectedStartingPointId: string | undefined;

    serviceMap?: ServiceMapType;
    unavailableServices: UnavailableService[];
}

export class InnerNewProcessButtonStartBar extends Component<
    INewProcessButtonStartBarProps,
    INewProcessButtonStartBarState
> {
    private abortableFetch = new AbortableFetch();
    private readonly newProcessApi: NewProcessApi = new NewProcessApi(this.abortableFetch);

    constructor(props: INewProcessButtonStartBarProps) {
        super(props);
        this.state = this.getNewProcessButtonStartBarEmptyState();
    }

    public async componentDidMount() {
        await this.loadTypeButtons();
        if (this.state.episodeCategoryMap) {
            await this.getCorrectEpisodeCategories(this.state.episodeCategoryMap);
        }

        const tempState: INewProcessButtonStartBarState = { ...this.state };

        if (this.props.defaultSelectedServiceType) {
            tempState.selectedServiceType = this.props.defaultSelectedServiceType;
        }
        if (this.props.defaultSelectedEpisodeCategory) {
            tempState.selectedEpisodeCategory = this.props.defaultSelectedEpisodeCategory;
            tempState.autoSelectedEpisodeCategory = true;
        }
        if (this.props.defaultSelectedService) {
            tempState.selectedService = this.props.defaultSelectedService;
            tempState.autoSelectedService = true;
        }

        await this.setState(tempState);

        if (this.props.defaultTriggerOnClick) {
            if (this.props.defaultTriggerOnClick.serviceType && this.props.defaultSelectedServiceType) {
                await this.onClickServiceType(this.props.defaultSelectedServiceType);
            }
            if (this.props.defaultTriggerOnClick.episodeCategory && this.props.defaultSelectedEpisodeCategory) {
                await this.onClickServiceName(this.props.defaultSelectedEpisodeCategory);
            }
            if (this.props.defaultTriggerOnClick.service && this.props.defaultSelectedService) {
                await this.onClickService(this.props.defaultSelectedService);
            }
        }

        if (this.props.onLoad) {
            this.props.onLoad();
        }

        if (this.props.patient) {
            this.loadUnavailableServicesForPatient(this.props.patient.id);
        }
    }

    public async componentDidUpdate(prevProps: INewProcessButtonStartBarProps) {
        if (this.props.patient && this.props.patient !== prevProps.patient) {
            this.loadUnavailableServicesForPatient(this.props.patient.id);
            await this.setState({ selectedService: undefined });
        }

        if (prevProps.defaultSelectedService && this.state.selectedService && !this.props.defaultSelectedService) {
            await this.setState({ autoSelectedService: false, selectedService: undefined });
        }
    }

    public async componentWillUnmount() {
        this.abortableFetch.abort();
    }

    public render() {
        const service = this.getService();
        let NewProcessCustomControl;
        if (service) {
            NewProcessCustomControl = newProcessLoadCustomControl(service, this.state.selectedActionWithProcedure);
        }

        return (
            <>
                {this.canRenderNameButtonRow() && (
                    <NewProcessNameButtonRow
                        episodeCategories={this.state.episodeCategories}
                        onClear={this.onClear}
                        onClick={this.onClickServiceName}
                        selectedEpisodeCategory={this.state.selectedEpisodeCategory}
                        showCloseButton={Boolean(this.state.selectedEpisodeCategory)}
                        unavailableServices={this.state.unavailableServices}
                    />
                )}
                {this.canRenderTypeButtonRow() && (
                    <NewProcessTypeButtonRow
                        label={this.props.label}
                        onClick={this.onClickServiceType}
                        scrollToBottom={this.props.scrollToBottom}
                        selectedServiceType={this.state.selectedServiceType || ServiceType.other}
                        serviceTypes={this.getCorrectServiceTypes()}
                        unavailableServices={this.state.unavailableServices}
                    />
                )}
                {this.canRenderServiceButtonRow() && (
                    <NewProcessServiceButtonRow
                        decisionAidPlacement={this.props.decisionAidPlacement}
                        onClick={this.onClickService}
                        selectedEpisodeCategory={this.state.selectedEpisodeCategory}
                        selectedService={this.state.selectedService}
                        selectedServiceType={this.state.selectedServiceType!}
                        services={this.getCorrectServices()}
                        unavailableServices={this.state.unavailableServices}
                    />
                )}
                {this.canRenderActionButtonRow() && (
                    <NewProcessActionButtonRow
                        onClick={this.onClickAction}
                        selectedActionWithProcedure={this.state.selectedActionWithProcedure}
                    />
                )}
                {this.canRenderStartingPointButtonRow() && (
                    <NewProcessStartingPointButtonRow
                        onClick={this.onClickStartingPoint}
                        selectedStartingPointId={this.state.selectedStartingPointId}
                        startingPoints={this.getAvailableStartingPoints()}
                    />
                )}
                {NewProcessCustomControl && (
                    <NewProcessCustomControl
                        service={service!}
                        setNewProcessCustomIdentifier={this.setNewProcessCustomIdentifier}
                    />
                )}
            </>
        );
    }

    private getService() {
        return (
            this.state.serviceMap &&
            this.state.selectedServiceType &&
            this.state.selectedEpisodeCategory &&
            this.state.selectedService &&
            this.getServiceMap(this.state.selectedServiceType) &&
            this.getServiceMap(this.state.selectedServiceType)![
                this.state.selectedEpisodeCategory as EpisodeCategory
            ] &&
            this.getServiceMap(this.state.selectedServiceType)![
                this.state.selectedEpisodeCategory as EpisodeCategory
            ]!.find((item) => item.id === this.state.selectedService!.serviceId)
        );
    }

    @bind
    private async onClear() {
        await this.setState({ ...this.getNewProcessButtonStartBarEmptyState() });
        await this.loadTypeButtons();
        if (this.props.onClear) {
            this.props.onClear();
        }
    }

    @bind
    private async onClickServiceType(serviceType: ServiceType) {
        if (serviceType === 'consultation' && this.state.selectedEpisodeCategory === 'mentalHealth') {
            addTrackEvent('ProcessStartWizard', 'Consultation clicked', 'User selects consultation service type');
        }
        await this.onClickService(undefined);
        await this.setState({ selectedServiceType: serviceType });

        await this.props.onServiceTypeSelected?.(serviceType);

        const services = this.getCorrectServices();

        if (services.length === 1) {
            await this.onClickService({
                newProcessCustomIdentifier: undefined,
                serviceId: services[0].id,
                withProcedure: services[0].serviceType === ServiceType.examination,
            });
        } else {
            await this.onClickService(undefined);
        }
    }

    @bind
    private async onClickServiceName(selectedEpisodeCategory: EpisodeCategory) {
        if (selectedEpisodeCategory === 'mentalHealth') {
            addTrackEvent('ProcessStartWizard', 'Mentalhealth clicked', 'User selects mentalhealth category');
        }
        if (this.state.episodeCategoryMap === undefined) {
            return;
        }

        await this.setState({ selectedEpisodeCategory });

        this.props.onEpisodeCategorySelected?.(selectedEpisodeCategory);

        await this.onClickService(undefined);
        await this.setState({ selectedServiceType: undefined });

        const selectedEpisodeCategoriesKeys = Object.keys(
            this.state.episodeCategoryMap[selectedEpisodeCategory!] as object
        );

        if (selectedEpisodeCategoriesKeys.length === 1) {
            this.onClickServiceType(selectedEpisodeCategoriesKeys[0] as ServiceType);
        }
    }

    @bind
    private async onClickService(selectedService: ISelectedService | undefined) {
        await this.setState({
            selectedActionWithProcedure: undefined,
            selectedService,
            selectedStartingPointId: undefined,
        });
        if (this.props.onServiceIdSelected) {
            const service = this.getService();
            const hasStartingPoints = (service && service.startingPoints && service.startingPoints.length > 0) || false;

            if (
                this.canRenderActionButtonRow() ||
                this.canRenderStartingPointButtonRow() ||
                !selectedService ||
                newProcessCanRenderCustomControl(service, undefined)
            ) {
                await this.props.onServiceIdSelected({
                    hasStartingPoints,
                    serviceId: selectedService?.serviceId,
                    withProcedure: selectedService?.withProcedure || false,
                });
                if (this.props.scrollToBottom) {
                    await this.props.scrollToBottom();
                }
            } else if (service && service.startingPoints && service.startingPoints.length === 1) {
                await this.props.onStartingPointSelected(service.startingPoints[0]);
                this.props.onServiceIdSelected({
                    hasStartingPoints,
                    serviceId: service.startingPoints[0].service.id,
                    withProcedure: false,
                });
            } else {
                await this.props.onServiceIdSelected({
                    hasStartingPoints,
                    serviceId: selectedService.serviceId,
                    withProcedure: selectedService.withProcedure,
                });
            }
        }
    }

    @bind
    private async onClickAction(actionWithProcedure: boolean | undefined) {
        await this.setState({ selectedActionWithProcedure: actionWithProcedure });
        if (this.props.onServiceIdSelected) {
            if (this.state.selectedService && actionWithProcedure !== undefined) {
                const service = this.getService();
                if (newProcessCanRenderCustomControl(service, actionWithProcedure)) {
                    await this.props.onServiceIdSelected({
                        serviceId: this.state.selectedService.serviceId,
                        withProcedure: actionWithProcedure,
                    });
                    if (this.props.scrollToBottom) {
                        await this.props.scrollToBottom();
                    }
                } else {
                    await this.props.onServiceIdSelected({
                        serviceId: this.state.selectedService.serviceId,
                        withProcedure: actionWithProcedure,
                    });
                }
            } else {
                await this.props.onServiceIdSelected({
                    serviceId: undefined,
                    withProcedure: false,
                });
            }
        }
    }

    @bind
    private async onClickStartingPoint(startingPoint: IServiceStartingPoint) {
        await this.setState({ selectedStartingPointId: startingPoint.id });
        if (this.props.onStartingPointSelected) {
            this.props.onStartingPointSelected(startingPoint);
        }
    }

    @bind
    private async setNewProcessCustomIdentifier(newProcessCustomIdentifier: string | undefined) {
        await this.setState({ newProcessCustomIdentifier });
        if (this.props.onServiceIdSelected) {
            if (newProcessCustomIdentifier && this.state.selectedService) {
                await this.props.onServiceIdSelected({
                    newProcessCustomIdentifier,
                    serviceId: this.state.selectedService.serviceId,
                    withProcedure: this.state.selectedActionWithProcedure || false,
                });
            } else {
                await this.props.onServiceIdSelected({
                    serviceId: undefined,
                    withProcedure: false,
                });
            }
        }
    }

    private async loadTypeButtons() {
        // if user is within a patient carepath and carepath has a flex and non flex version
        // we want to only show the buttons for the correct version to avoid confusion
        // a flex patient carepath is /patient/:patientId/:episodeCategory
        // a not flex/old patient carepath is /episode?patientId=:patientId&episodeCategory=:episodeCategory
        let serviceMap = await this.newProcessApi.createServiceMap(
            this.props.patient?.id,
            !!this.props.router.params.episodeCategory,
            this.props.router.searchParams.has('episodeCategory')
        );

        const serviceTypes = this.getServiceTypes(serviceMap);
        if (serviceTypes.find((value) => value === ServiceType.inquiry)) {
            if (serviceTypes.find((value) => value === ServiceType.examination)) {
                serviceMap = this.migrateInquiryToExamination(serviceMap);
            } else {
                serviceMap.examination = { ...serviceMap.inquiry };
                delete serviceMap.inquiry;
            }
        }
        if (serviceTypes.find((value) => value === ServiceType.referralSmall)) {
            if (serviceTypes.find((value) => value === ServiceType.referral)) {
                serviceMap = this.migrateReferralSmallToReferral(serviceMap);
            } else {
                serviceMap.referral = { ...serviceMap.referralSmall };
                delete serviceMap.referralSmall;
            }
        }

        let episodeCategoryMap: EpisodeCategoryMapType = {};

        Object.entries(serviceMap).forEach(([serviceKey, episodeCategory]) => {
            Object.entries(episodeCategory as unknown as EpisodeCategory).forEach(
                ([episodeCategoryKeyString, service]) => {
                    const episodeCategoryKey = episodeCategoryKeyString as keyof EpisodeCategoryMapType;

                    if (episodeCategoryMap[episodeCategoryKey] === undefined) {
                        episodeCategoryMap[episodeCategoryKey] = {};
                    }

                    episodeCategoryMap[episodeCategoryKey] = {
                        ...episodeCategoryMap[episodeCategoryKey],
                        [serviceKey]: service,
                    };
                }
            );
        });

        const { general, ...episodeCategoryMapRest } = episodeCategoryMap;

        episodeCategoryMap = { ...episodeCategoryMapRest };

        if (general !== undefined) {
            const { ...generalRest } = general!;

            if (generalRest !== undefined && Object.keys(generalRest).length) {
                episodeCategoryMap = {
                    ...episodeCategoryMap,
                    general: { ...generalRest },
                };
            }
        }

        await this.setState({ episodeCategoryMap });
        await this.setState({ serviceMap });
    }

    private getServiceMap(serviceType: ServiceType) {
        if (this.state.serviceMap) {
            let mapServiceType = serviceType;
            if (serviceType === ServiceType.referralSmall) {
                mapServiceType = ServiceType.referral;
            }

            return this.state.serviceMap[mapServiceType];
        }
    }

    private getEpisodeCategoryMap(episodeCategory: EpisodeCategory) {
        if (this.state.episodeCategoryMap) {
            return this.state.episodeCategoryMap[episodeCategory];
        }
    }

    private getServiceTypes(serviceMap: ServiceMapType | undefined) {
        if (!serviceMap) {
            throw new Error('No servicemap initialized');
        }

        return Object.keys(serviceMap) as ServiceType[];
    }

    private migrateInquiryToExamination(ServiceMap: ServiceMapType) {
        const migratedMap = this.migrateTwoServiceTypes(ServiceMap, ServiceType.inquiry, ServiceType.examination);
        delete migratedMap.inquiry;

        return migratedMap;
    }

    private migrateReferralSmallToReferral(ServiceMap: ServiceMapType) {
        const migratedMap = this.migrateTwoServiceTypes(ServiceMap, ServiceType.referralSmall, ServiceType.referral);
        delete migratedMap.referralSmall;

        return migratedMap;
    }

    private migrateTwoServiceTypes(
        serviceMap: ServiceMapType,
        fromServiceType: ServiceType,
        toServiceType: ServiceType
    ) {
        const map = { ...serviceMap };
        if (!map[fromServiceType] || !map[toServiceType]) {
            throw new Error('Expected map to contain inquiry and examination');
        }

        const toServiceTypeContent = Object.keys(map[toServiceType]!) as EpisodeCategory[];
        const fromServiceTypeContent = Object.keys(map[fromServiceType]!) as EpisodeCategory[];

        for (const fromServiceTypeItem of fromServiceTypeContent) {
            if (toServiceTypeContent.includes(fromServiceTypeItem)) {
                map[toServiceType]![fromServiceTypeItem] = map[toServiceType]![fromServiceTypeItem]!.concat(
                    map[fromServiceType]![fromServiceTypeItem]!
                );
            } else {
                map[toServiceType]![fromServiceTypeItem] = map[fromServiceType]![fromServiceTypeItem];
            }
        }

        return map;
    }

    private getCorrectServiceTypes() {
        if (!this.state.episodeCategoryMap || !this.state.selectedEpisodeCategory) {
            return [];
        }
        const episodeCategories = this.state.episodeCategoryMap;
        const selectedEpisodeCategory = this.state.selectedEpisodeCategory;
        const serviceTypes = episodeCategories[selectedEpisodeCategory] as EpisodeCategoryServicesMapType;

        const serviceTypeArray = Object.entries(serviceTypes).map(([serviceTypeName, servicesArray]) => {
            return this.getProcessStepWithOneService(servicesArray, serviceTypeName);
        }) as IServiceTypeWithOneService[];

        return serviceTypeArray;
    }
    private async getCorrectEpisodeCategories(episodeCategoryMap: EpisodeCategoryMapType) {
        const episodeCategories = Object.entries(episodeCategoryMap).map(([episodeCategoryName, serviceTypes]) => {
            const serviceTypeNames = Object.entries(serviceTypes);
            if (serviceTypeNames.length === 1) {
                const serviceTypeName = serviceTypeNames[0];

                return this.getProcessStepWithOneService(serviceTypeName[1], episodeCategoryName);
            }

            return { name: episodeCategoryName };
        }) as IEpisodeCategoryOneService[];

        await this.setState({ episodeCategories });
    }

    private getProcessStepWithOneService(dataArray: IService[], name: string) {
        const oneTypeServiceId = this.getOneTypeOfServiceId(dataArray);

        if (oneTypeServiceId) {
            return { name, oneTypeServiceId };
        }

        return { name };
    }

    private getOneTypeOfServiceId(services: IService[] | undefined) {
        if (services?.every((service) => service.id === services[0].id)) {
            return services[0].id;
        }

        return undefined;
    }

    private getCorrectServices() {
        if (this.state.serviceMap) {
            const servicesByEpisodeCategory = this.getEpisodeCategoryMap(
                this.state.selectedEpisodeCategory as EpisodeCategory
            );
            if (servicesByEpisodeCategory) {
                const services = servicesByEpisodeCategory[this.state.selectedServiceType!];
                if (services) {
                    return uniqBy(services, (service) => service.id);
                }
            }
        }

        return [];
    }

    private canRenderTypeButtonRow() {
        if (this.state.episodeCategoryMap && this.state.selectedEpisodeCategory) {
            const totalServiceTypes = Object.keys(
                this.state.episodeCategoryMap[this.state.selectedEpisodeCategory] ?? []
            ).length;

            if (this.props.showServiceTypeBar) {
                return totalServiceTypes > 0;
            }

            return totalServiceTypes > 1;
        }
    }

    private canRenderNameButtonRow() {
        if (this.props.hideEpisodeCategoryBar || !this.state.episodeCategories) {
            return false;
        }

        return this.state.episodeCategories.length > 0;
    }

    private canRenderServiceButtonRow() {
        return this.getCorrectServices().length > 0;
    }

    private canRenderActionButtonRow() {
        const selectedServiceId = this.state.selectedService && this.state.selectedService.serviceId;

        if (
            this.state.selectedServiceType !== ServiceType.examination ||
            !selectedServiceId ||
            !this.state.serviceMap ||
            !this.state.serviceMap.examination ||
            !this.state.selectedEpisodeCategory
        ) {
            return false;
        }

        const serviceOptions = this.state.serviceMap.examination[this.state.selectedEpisodeCategory as EpisodeCategory];

        if (!serviceOptions) {
            return false;
        }

        const selectedService = serviceOptions.find((option) => option.id === selectedServiceId);

        if (!selectedService) {
            return false;
        }

        const otherOptionForSelectedService = serviceOptions.find(
            (option) => option.code === selectedService.code && option.serviceType !== selectedService.serviceType
        );

        return Boolean(otherOptionForSelectedService);
    }

    private canRenderStartingPointButtonRow() {
        return this.getAvailableStartingPoints().length > 1;
    }

    private getAvailableStartingPoints() {
        const selectedService = this.getService();
        if (!selectedService || !selectedService.startingPoints) {
            return [];
        }

        return selectedService.startingPoints;
    }

    private getNewProcessButtonStartBarEmptyState() {
        const newEmptyState: INewProcessButtonStartBarState = {
            autoSelectedEpisodeCategory: false,
            autoSelectedService: false,
            episodeCategories: [],
            episodeCategoriesByServiceType: {},
            newProcessCustomIdentifier: undefined,
            selectedActionWithProcedure: undefined,
            selectedEpisodeCategory: undefined,
            selectedService: undefined,
            selectedServiceType: undefined,
            selectedStartingPointId: undefined,
            unavailableServices: [],
        };

        if (this.state.autoSelectedEpisodeCategory) {
            newEmptyState.autoSelectedEpisodeCategory = this.state.autoSelectedEpisodeCategory;
            newEmptyState.selectedEpisodeCategory = this.state.selectedEpisodeCategory;
        }

        return newEmptyState;
    }

    private async loadUnavailableServicesForPatient(patientId: string) {
        const result = await findUnavailableServices([patientId]);

        await this.setState({
            unavailableServices: result[patientId],
        });
    }
}

export const NewProcessButtonStartBar = withRouter(InnerNewProcessButtonStartBar);
